/*
 * cf-gate-only-latch.js — Click Fencing
 *
 * Pete, 2026-10-15 (Pravin CF-1026): a gate-only section (a gate between its own two posts, no panels) is
 * "gate + 2 posts + hinges + a double sided lockable push button latch". Pete: "Use the D&D Lokk Latch Deluxe
 * push button (LLD3LD black / LLD3KDWT white) as the gate-only latch - add it to the store and the calculator."
 *
 * Blade, Flat Top and PerfMesh. For every gate-only section this sets the section's latch to the Lokk Latch
 * (Satin White fence -> LLD3KDWT, every other colour -> LLD3LD) and puts the product into the latch lists each
 * calculator bills from (bladeLatches / flatTopLatches / perfmeshProducts.latches). The calculator then quotes,
 * shows and carts it through its own code. The Lokk Latch is offered ONLY on gate-only sections; a section that
 * gets panels again goes back to the calculator's default latch.
 *
 * Product data (variant ids, prices) is read live from /products/dd-lokk-latch-deluxe-push-button.js, so nothing
 * here goes stale. No product = no change (the patch stands down).
 */
(function () {
  "use strict";
  if (!/(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence)/.test(location.pathname)) return;

  var HANDLE = "dd-lokk-latch-deluxe-push-button";
  var SKUS = { black: "LLD3LD", white: "LLD3KDWT" };
  var TITLE = "D&D Lokk Latch Deluxe push button, lockable both sides";
  var IS_MESH = /perfmesh-fence/.test(location.pathname);
  var DEFAULT_LATCH = IS_MESH ? "SSPSKL" : "SL-50K";
  var entries = null; // { LLD3LD: {sku,title,price(cents),id}, LLD3KDWT: {...} }
  var TRIES = 0, MAX_TRIES = 200, busy = false;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function isLokk(sku) { return sku === SKUS.black || sku === SKUS.white; }

  // Same test as cf-gate-only-section.js: a solved section with a gate and no panels.
  function isGateOnly(fc, n) {
    try {
      var s = fc.state.sections[n], c = fc.state.optimizedConfigs && fc.state.optimizedConfigs[n];
      if (!s || !s.hasGate || !c || !c.valid || (c.gatePanels || 0) <= 0) return false;
      if (!((parseInt(c.total, 10) || 0) > 0) && !c.perfomesh) return false;
      var noPanels = c.numPanels === 0 || (Array.isArray(c.panelWidths) && c.panelWidths.length === 0);
      return noPanels && (parseInt(s.length, 10) || 0) > 0;
    } catch (e) { return false; }
  }

  function wantSku(fc, s) {
    var raw = String((s && s.color) || (fc.state.sameForAll && fc.state.shared && fc.state.shared.color) || "").toLowerCase();
    return raw.indexOf("white") >= 0 ? SKUS.white : SKUS.black;
  }

  function loadProduct() {
    return fetch("/products/" + HANDLE + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants) return;
        var out = {};
        p.variants.forEach(function (v) {
          if (isLokk(v.sku)) out[v.sku] = { sku: v.sku, title: TITLE, price: v.price, id: v.id, available: true, color: v.sku === SKUS.white ? "white" : "black" };
        });
        if (out[SKUS.black] || out[SKUS.white]) entries = out;
      })
      .catch(function () {});
  }

  // Put the Lokk Latch into every list the three calculators bill a latch from.
  function injectData(fc) {
    try {
      var sd = fc.shopifyData || (fc.shopifyData = {});
      sd.products = sd.products || {};
      sd.collections = sd.collections || {};
      sd.collections.perfmeshProducts = sd.collections.perfmeshProducts || {};
      var lists = [];
      if (Array.isArray(sd.products.bladeLatches)) lists.push(sd.products.bladeLatches);
      if (Array.isArray(sd.products.flatTopLatches)) lists.push(sd.products.flatTopLatches);
      if (!Array.isArray(sd.collections.perfmeshProducts.latches)) sd.collections.perfmeshProducts.latches = [];
      lists.push(sd.collections.perfmeshProducts.latches);
      // 21 Oct 2026 (Matt CF-1039): the calculators' own latches carry no variant id, and PerfMesh falls back to the
      // FIRST latch in the list that has one - the Lokk Latch - so a normal gate billed "SSPSKL $113.95" with the Lokk
      // variant 67747623567454. Give each own latch its variant by its SKU before the Lokk entries go in.
      lists.forEach(function (arr) {
        arr.forEach(function (l) {
          if (l && !l.id && l.sku && !isLokk(l.sku) && typeof window.CF_VARIANT === "function") {
            var v = window.CF_VARIANT(l.sku);
            if (v) l.id = Number(v);
          }
        });
      });
      lists.forEach(function (arr) {
        Object.keys(entries).forEach(function (sku) {
          if (!arr.some(function (l) { return l && l.sku === sku; })) arr.push(Object.assign({}, entries[sku]));
        });
      });
      // Not added to skuVariantMap: PerfMesh names a latch found there from its own label list, which has no Lokk Latch
      // ("Gate Latch (LLD3KDWT)"). Every quote line already carries the variant id, which is what the cart uses.
    } catch (e) {}
  }

  // The section's latch drop-down: the Lokk Latch is listed (and chosen) on a gate-only section only.
  function syncSelect(n, sku) {
    try {
      var sel = document.getElementById("section-" + n + "-latch-type");
      if (!sel) return;
      [].slice.call(sel.options).forEach(function (o) { if (isLokk(o.value) && o.value !== sku) o.remove(); });
      if (sku) {
        if (![].some.call(sel.options, function (o) { return o.value === sku; })) {
          var o = document.createElement("option"); o.value = sku; o.textContent = TITLE; sel.appendChild(o);
        }
        sel.value = sku;
      } else if (isLokk(sel.value)) {
        sel.value = DEFAULT_LATCH;
      }
    } catch (e) {}
  }

  // Returns true when a section's latch was changed (so the caller re-prices it).
  function applySection(fc, n) {
    var s = fc.state.sections[n];
    if (!s) return false;
    if (isGateOnly(fc, n)) {
      var sku = wantSku(fc, s);
      if (!entries[sku]) sku = entries[SKUS.black] ? SKUS.black : SKUS.white;
      syncSelect(n, sku);
      if (s.latchType !== sku) { s.latchType = sku; return true; }
      return false;
    }
    syncSelect(n, null);
    if (isLokk(s.latchType)) { s.latchType = DEFAULT_LATCH; return true; }
    return false;
  }

  function sectionNums(fc) {
    try { return Object.keys(fc.state.sections || {}).map(Number).filter(function (n) { return !isNaN(n); }); } catch (e) { return []; }
  }

  function sweep() {
    var fc = inst();
    if (!fc || !fc.state || !entries || busy) return;
    busy = true;
    try {
      injectData(fc);
      var P = Object.getPrototypeOf(fc), orig = P.__cfGoLatchOrigUCR || fc.updateCalculationResults;
      var changed = false;
      sectionNums(fc).forEach(function (n) {
        if (applySection(fc, n)) {
          changed = true;
          try { orig.call(fc, n); } catch (e) {}
        }
      });
      if (changed && typeof fc.updateQuoteDisplay === "function") { try { fc.updateQuoteDisplay(); } catch (e) {} }
    } catch (e) {}
    busy = false;
  }

  var timer = null;
  function schedule() { if (timer) clearTimeout(timer); timer = setTimeout(sweep, 250); }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfGateOnlyLatch) return true;
    if (typeof P.updateCalculationResults === "function") {
      var o = P.updateCalculationResults;
      P.__cfGoLatchOrigUCR = o;
      P.updateCalculationResults = function (n) {
        var r = o.apply(this, arguments);
        // optimizedConfigs is swapped asynchronously after a field change, so check again shortly after.
        try { if (entries && !busy && applySection(this, n)) { busy = true; try { o.call(this, n); } finally { busy = false; } } } catch (e) {}
        schedule();
        return r;
      };
    }
    try {
      var root = document.querySelector(".calculator-content") || document.body;
      if (window.MutationObserver) new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    } catch (e) {}
    P.__cfGateOnlyLatch = true;
    loadProduct().then(function () {
      try { console.log("[cf-gate-only-latch] installed, product " + (entries ? "found" : "NOT found - standing down")); } catch (e) {}
      schedule();
    });
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", tryInstall); else tryInstall();
})();
